/**
 * Mapa inverso celda del Excel → dónde se captura en la app ("Mis datos → sección → campo").
 * Lo usan el grounding del asesor (cuando el miembro capturó en la app, source = "form") y Mi foto (links a la sección).
 * Puro: sin DOM ni React. Las rutas coinciden con src/features/datos/secciones.ts.
 */
import type { ModelInputs } from "../../types";

export type SeccionApp = "sobre-ti" | "diagnostico" | "rueda" | "flujo" | "balance" | "objetivos" | "identidad" | "proposito" | "estrategia" | "portafolio" | "sistema" | "foto";
export interface CampoDescrito { seccion: SeccionApp; seccionEtiqueta: string; grupo?: string; etiqueta: string; ruta: string; row?: number }

const SECCION: Record<SeccionApp, string> = {
  "sobre-ti": "Sobre ti", diagnostico: "Diagnóstico de diseño patrimonial", rueda: "Diagnóstico de riqueza 360", flujo: "Mis ingresos y mis gastos", balance: "Mis activos y mis pasivos", objetivos: "Tus objetivos",
  identidad: "Quién soy", proposito: "Propósito y película de vida", estrategia: "Mi estrategia", portafolio: "Mi portafolio por bucket", sistema: "Mi sistema patrimonial", foto: "Mi foto",
};

const parse = (ref: string): { hoja: string; col: string; row: number | null; rango: boolean } => {
  const i = ref.lastIndexOf("!");
  const hoja = (i >= 0 ? ref.slice(0, i) : ref).trim();
  const celda = i >= 0 ? ref.slice(i + 1) : "";
  const m = /^\$?([A-Z]+)\$?(\d+)/.exec(celda);
  return { hoja, col: m?.[1] ?? "", row: m ? Number(m[2]) : null, rango: celda.includes(":") };
};

const d = (seccion: SeccionApp, etiqueta: string, ancla?: string, grupo?: string, row?: number): CampoDescrito =>
  ({ seccion, seccionEtiqueta: SECCION[seccion], grupo, etiqueta, ruta: seccion === "foto" ? "/foto" : `/datos/${seccion}${ancla ? `#${ancla}` : ""}`, row });

const nombreFila = (inputs: ModelInputs | undefined, tabla: "assets" | "liabilities" | "objectives" | "incomes" | "expenses", row: number): string | null => {
  if (!inputs) return null;
  const t = tabla === "assets" ? inputs.balance.assets : tabla === "liabilities" ? inputs.balance.liabilities : tabla === "objectives" ? inputs.life.objectives : tabla === "incomes" ? inputs.flow.incomes : inputs.flow.expenses;
  const r = t.find((x) => x.row === row) as Record<string, unknown> | undefined;
  const n = r?.what ?? r?.name ?? r?.concept ?? r?.source;
  return typeof n === "string" && n ? n : null;
};

/** Describe una referencia "Hoja!Celda" (o rango) en términos de la app. null si no se reconoce. */
export function describeCell(ref: string, inputs?: ModelInputs): CampoDescrito | null {
  const { hoja, col, row, rango } = parse(ref);
  const h = hoja.toLowerCase();
  if (h.startsWith("índice y setup") || h.startsWith("indice y setup")) {
    const et: Record<number, string> = { 7: "Tu edad", 8: "Año del modelo", 9: "Hasta qué edad proyectar", 10: "Moneda" };
    return d("sobre-ti", (row ? et[row] : undefined) ?? "Tus datos básicos");
  }
  if (h.startsWith("0 · diagnóstico")) {
    // Filas del Excel WX: 14-31 preguntas de los pasos 3-5 · 45/54 promedios de los pasos 1-2 · 90+ Rueda 360.
    if (row && row >= 14 && row <= 31) return d("diagnostico", "Las preguntas de los pasos 3, 4 y 5");
    if (row && row >= 90) return d("rueda", "Las 12 preguntas de la Rueda de Riqueza 360");
    if (row === 45 || row === 54) return d("diagnostico", `Las preguntas del paso ${row === 45 ? 1 : 2}`);
    return d("diagnostico", "Tu IDP");
  }
  // Con `inputs`, una fila pertenece a la tabla que la contiene (el Excel del miembro puede traer filas insertadas o borradas);
  // sin `inputs`, valen las filas de la plantilla.
  const en = (tabla: { row: number }[] | undefined, lo: number, hi: number): boolean => row !== null && (inputs ? !!tabla && tabla.some((x) => x.row === row) : row >= lo && row <= hi);
  if (h.startsWith("3a · flujo")) {
    if (en(inputs?.flow.incomes, 6, 16)) { const n = rango ? null : nombreFila(inputs, "incomes", row!); return d("flujo", n ? `"${n}"` : "Mis ingresos", "ingresos", "Mis ingresos", rango ? undefined : row!); }
    if (en(inputs?.flow.expenses, 24, 54)) { const n = rango ? null : nombreFila(inputs, "expenses", row!); return d("flujo", n ? `"${n}"` : "Mis gastos", "gastos", "Mis gastos", rango ? undefined : row!); }
    if (row && row >= 80) return d("flujo", "Reflexiones del taller (solo en el Excel)");
    return d("flujo", "Tu flujo");
  }
  if (h.startsWith("3b · balance")) {
    if (en(inputs?.balance.assets, 6, 45)) {
      if (rango) return d("balance", "Mis activos", "activos", "Mis activos");
      const n = nombreFila(inputs, "assets", row!);
      const campo: Record<string, string> = { A: "¿Qué es?", B: "Tipo", C: "Tipo", D: "¿Cuánto vale hoy?", J: "Corregir bucket (opciones avanzadas)", O: "Alerta de concentración" };
      return d("balance", `${n ? `"${n}" → ` : ""}${campo[col] ?? "Activo"}`, `activo-${row}`, "Mis activos", row!);
    }
    if (en(inputs?.balance.liabilities, 51, 63)) {
      if (rango) return d("balance", "Mis pasivos", "deudas", "Mis pasivos");
      const n = nombreFila(inputs, "liabilities", row!);
      const campo: Record<string, string> = { A: "¿Qué debo?", B: "Saldo hoy", C: "Pago mensual", D: "Tasa anual", E: "¿Qué tipo de deuda es?" };
      return d("balance", `${n ? `"${n}" → ` : ""}${campo[col] ?? "Deuda"}`, `deuda-${row}`, "Mis pasivos", row!);
    }
    return d("balance", "Tus totales (se calculan de lo que tienes y debes)", "activos");
  }
  if (h.startsWith("2 · vida")) {
    // La fila del fondo es la anterior al primer objetivo (28 en la plantilla).
    const filaFondo = inputs?.life.objectives[0] ? inputs.life.objectives[0].row - 1 : 28;
    if (row === filaFondo) return d("objetivos", "Fondo de emergencia", "fondo", "Fondo de emergencia");
    if (en(inputs?.life.objectives, 29, 38)) {
      if (rango) return d("objetivos", "Tus objetivos", undefined, "Tus objetivos");
      const n = nombreFila(inputs, "objectives", row!);
      const campo: Record<string, string> = { A: "Prioridad (orden en la lista)", B: "Nombre", C: "¿A qué edad?", E: "Tipo", F: "Costo en pesos de hoy", J: "Cambiar la estrategia" };
      return d("objetivos", `${n ? `"${n}" → ` : ""}${campo[col] ?? "Objetivo"}`, `objetivo-${row}`, "Tus objetivos", row!);
    }
    if (row && row >= 5 && row <= 16) return d("proposito", "Tu propósito patrimonial");
    if (row && row >= 19 && row <= 23) return d("proposito", "Tu película de vida");
    if (row === 44) return d("objetivos", "Capital libre (avanzado)");
    return d("objetivos", "Prioridades de tus objetivos (el orden de la lista)");
  }
  if (h.startsWith("1 · identidad")) return d("identidad", row && row >= 40 ? "Tu arquetipo" : row && row >= 23 ? "Tu mapa de necesidades" : "Tu historia y tus valores");
  if (h.startsWith("4 · estrategia")) {
    if (row === 40 || row === 43) return d("estrategia", row === 40 ? "Mi desalineación más grande" : "En cuánto tiempo la cierro");
    if (row === 56 || row === 61) return d("estrategia", row === 56 ? "Tasa real (ajustes avanzados)" : "Aporte anual (ajustes avanzados)");
    if (row && row >= 6 && row <= 15 && col === "H") return d("estrategia", "Activos concretos por objetivo");
    return d("foto", "Tu proyección (Mi foto)");
  }
  if (h.startsWith("4b · mi portafolio")) {
    const b = /\(([^)]+)\)/.exec(hoja)?.[1];
    return d("portafolio", b ? `Bloque ${b}` : "Tu portafolio por bucket");
  }
  if (h.startsWith("5 · sistema")) {
    if (row && row >= 6 && row <= 16) return d("sistema", "Mi equipo");
    if (row && row >= 21 && row <= 26) return d("sistema", "Mi cadencia");
    if (row && row >= 33 && row <= 43) return d("sistema", "El filtro de 30 segundos");
    if (row && row >= 46 && row <= 48) return d("sistema", "Mi plan de 30 días");
    if (row === 53) return d("sistema", "Mi acto de coraje");
    return d("sistema", "Tus límites");
  }
  if (h.startsWith("mi foto")) return d("foto", "Mi foto");
  return null;
}

/** "Mis datos → Mis activos y mis pasivos → Mis pasivos → "Tarjeta" → ¿Qué tipo de deuda es?" (o la ref original si no se reconoce). */
export function describeCellText(ref: string, inputs?: ModelInputs): string {
  const c = describeCell(ref, inputs);
  if (!c) return ref;
  return c.seccion === "foto" ? `Mi foto → ${c.etiqueta}` : `Mis datos → ${c.seccionEtiqueta}${c.grupo && !c.etiqueta.startsWith(c.grupo) ? ` → ${c.grupo}` : ""} → ${c.etiqueta}`;
}
export const describeCells = (refs: string[], inputs?: ModelInputs) => refs.map((r) => describeCellText(r, inputs));
